import { collection } from '@/infra/collection'
import {
  type Bundle as BundleProto,
  CreateBundleRequest,
  ListBundlesRequest,
  GetWorkspacesWithBundlesRequest,
  EditBundleRequest,
  BundleUpdateOptions,
  ArchiveBundleRequest,
  UnarchiveBundleRequest,
  BulkEditBundlePricesRequest,
  BundlePrice,
  BulkArchiveBundlesRequest,
  type WorkspaceWithBundles as WorkspaceWithBundlesProto,
} from '@taxfyle/api-internal/internal/bundle_pb'
import { Bundle, type BundleProps } from '@/features/admin/bundle/Bundle'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import {
  safeTimestampToDate,
  timestampToDate,
  safeDecimalToNumber,
  numberToDecimal,
} from '@/utils/grpc-util'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type AdminApi } from '@/features/admin/api/admin-api'
import {
  WorkspaceWithBundles,
  type WorkspaceWithBundlesProps,
} from './WorkspaceWithBundles'
import { Id } from '@/infra/id'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import {
  mapBundleBaseItemProtoToProps,
  mapBundleItemQuantityToProto,
  mapBundleProtoToIncludedItemsProps,
} from './bundle-mappers'
import { BundleRequestQueryFilterBuilder } from './bundle-request-query-filter-builder'
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb'

export interface BundleItemQuantity {
  inventoryItemId: Id
  includedQuantity: number
}

export interface CreateBundleParams {
  name: string
  description: string
  price: number
  workspaceId: Id
  baseItemId: Id
  includedItems: readonly BundleItemQuantity[]
}

export interface EditBundleParams {
  id: Id
  name: string
  description: string
  price: number
  baseItemId: Id
  includedItems: readonly BundleItemQuantity[]
}

/**
 * A single bundle's new price, sent to the bulk price-edit endpoint. Both the
 * discount and edit-price flows resolve to a list of these.
 */
export interface BundlePriceUpdate {
  bundleId: Id
  price: number
}

export interface BulkEditBundlePricesParams {
  workspaceId: Id
  priceUpdates: readonly BundlePriceUpdate[]
}

export interface BulkArchiveBundlesParams {
  workspaceId: Id
  ids: readonly Id[]
}

/**
 * The bundles the server skipped. Any id that was sent and does not appear in
 * one of these lists was archived. Re-archiving an already-archived bundle is
 * a harmless no-op, so those ids come back in `alreadyArchivedIds`.
 */
export interface BulkArchiveBundlesResult {
  notFoundIds: string[]
  alreadyArchivedIds: string[]
}

/**
 * The bundles the server skipped. Any id that was sent and does not appear in
 * one of these lists was repriced successfully.
 */
export interface BulkEditBundlePricesResult {
  notFoundIds: string[]
  archivedIds: string[]
}

/**
 * Whether to list active or archived bundles.
 */
export type BundleArchivedFilterOptions = 'Active' | 'Archived'

export interface ListBundlesQuery extends CursorPageQuery {
  workspaceId: Id
  archivedStatus: BundleArchivedFilterOptions
  orderBy: string
}

export class BundleStore {
  /**
   * The bundles.
   */
  readonly bundles = collection<Bundle, Id, BundleProps>({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Bundle(),
  })

  /**
   * The workspaces that have bundles.
   */
  readonly workspacesWithBundles = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new WorkspaceWithBundles(),
  })

  constructor(
    private readonly _api: AdminApi,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {}

  /**
   * Creates a bundle.
   */
  async create(params: CreateBundleParams) {
    const request = new CreateBundleRequest()
      .setName(params.name)
      .setDescription(params.description)
      .setPrice(numberToDecimal(params.price))
      .setWorkspaceId(Id.assert(params.workspaceId))
      .setBaseItemId(Id.assert(params.baseItemId))
      .setIncludedItemsList(
        params.includedItems.map(mapBundleItemQuantityToProto),
      )

    await this._api.bundles.createBundle(request)
  }

  /**
   * Lists a workspace's bundles.
   */
  async listBundles(
    query: ListBundlesQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Bundle>> {
    const request = new ListBundlesRequest()

    if (query.workspaceId) {
      request.setWorkspaceId(Id.assert(query.workspaceId))
    }
    if (query.limit) {
      request.setPageSize(query.limit)
    }
    if (query.after) {
      request.setPageToken(query.after)
    }
    request.setFilter(
      new BundleRequestQueryFilterBuilder()
        .withArchivedStatus(query.archivedStatus)
        .build(),
    )
    request.setOrderBy(query.orderBy)

    const response = await this._api.bundles.listBundles(request)
    signal?.throwIfAborted()

    return {
      data: response
        .getBundlesList()
        .map((bundle) => this.bundles.set(this._mapBundleProtoToProps(bundle))),
      next: response.getNextPageToken(),
    }
  }

  /**
   * Fetches the workspaces that have bundles.
   */
  async fetchWorkspacesWithBundles(signal?: AbortSignal) {
    const request = new GetWorkspacesWithBundlesRequest()

    const response = await this._api.bundles.getWorkspacesWithBundles(request)
    signal?.throwIfAborted()

    this.workspacesWithBundles.reset(
      response
        .getWorkspacesList()
        .map((w) => this._mapWorkspaceWithBundlesProtoToProps(w)),
    )
  }

  /**
   * Edits a bundle.
   */
  async edit(params: EditBundleParams) {
    const options = new BundleUpdateOptions()
      .setName(params.name)
      .setDescription(params.description)
      .setPrice(numberToDecimal(params.price))
      .setBaseItemId(Id.assert(params.baseItemId))
      .setIncludedItemsList(
        params.includedItems.map(mapBundleItemQuantityToProto),
      )

    const fieldMask = new FieldMask().setPathsList([
      'name',
      'description',
      'price',
      'base_item_id',
      'included_items',
    ])

    const request = new EditBundleRequest()
      .setId(Id.assert(params.id))
      .setOptions(options)
      .setUpdateMask(fieldMask)

    await this._api.bundles.editBundle(request)

    this.bundles.get(params.id)?.set({
      name: params.name,
      description: params.description,
      price: params.price,
    })
  }

  /**
   * Archives a bundle.
   */
  async archive(id: Id) {
    const request = new ArchiveBundleRequest().setId(Id.assert(id))

    await this._api.bundles.archiveBundle(request)

    this.bundles.get(id)?.set({ archived: true })
  }

  /**
   * Unarchives a bundle.
   */
  async unarchive(id: Id) {
    const request = new UnarchiveBundleRequest().setId(Id.assert(id))

    await this._api.bundles.unarchiveBundle(request)

    this.bundles.get(id)?.set({ archived: false })
  }

  /**
   * Sets the prices of several bundles.
   */
  async bulkEditPrices({
    workspaceId,
    priceUpdates,
  }: BulkEditBundlePricesParams): Promise<BulkEditBundlePricesResult> {
    const request = new BulkEditBundlePricesRequest()
      .setWorkspaceId(Id.assert(workspaceId))
      .setBundlePricesList(
        priceUpdates.map((update) =>
          new BundlePrice()
            .setBundleId(Id.assert(update.bundleId))
            .setPrice(numberToDecimal(update.price)),
        ),
      )

    const response = await this._api.bundles.bulkEditBundlePrices(request)
    const result = {
      notFoundIds: response.getNotFoundIdsList(),
      archivedIds: response.getArchivedIdsList(),
    }

    const skippedIds = new Set([...result.notFoundIds, ...result.archivedIds])
    for (const update of priceUpdates) {
      if (!skippedIds.has(Id.assert(update.bundleId))) {
        this.bundles.get(update.bundleId)?.set({ price: update.price })
      }
    }

    return result
  }

  /**
   * Archives several bundles.
   */
  async bulkArchive({
    workspaceId,
    ids,
  }: BulkArchiveBundlesParams): Promise<BulkArchiveBundlesResult> {
    const request = new BulkArchiveBundlesRequest()
      .setWorkspaceId(Id.assert(workspaceId))
      .setIdsList(ids.map(Id.assert))

    const response = await this._api.bundles.bulkArchiveBundles(request)
    const result = {
      notFoundIds: response.getNotFoundIdsList(),
      alreadyArchivedIds: response.getAlreadyArchivedIdsList(),
    }

    const notFoundIds = new Set(result.notFoundIds)
    for (const id of ids) {
      if (!notFoundIds.has(Id.assert(id))) {
        this.bundles.get(id)?.set({ archived: true })
      }
    }

    return result
  }

  private _mapWorkspaceWithBundlesProtoToProps(
    proto: WorkspaceWithBundlesProto,
  ): WorkspaceWithBundlesProps {
    const workspaceId = Id.assert(proto.getWorkspaceId())
    return {
      id: workspaceId,
      workspace: this._primeWorkspace({ id: workspaceId }) ?? Workspace.none,
    }
  }

  private _mapBundleProtoToProps(proto: BundleProto): BundleProps {
    const archivedBy = proto.getArchivedBy()
    const dateArchived = proto.getDateArchived()
    const lastEditedBy = proto.getLastEditedBy()
    const dateLastEdited = proto.getDateLastEdited()
    const baseItem = proto.getBaseItem()

    return {
      id: Id.assert(proto.getId()),
      workspace:
        this._primeWorkspace({ id: Id.assert(proto.getWorkspaceId()) }) ??
        Workspace.none,
      archived: proto.getArchived(),
      name: proto.getName(),
      description: proto.getDescription(),
      price: safeDecimalToNumber(proto.getPrice()),
      createdBy: this._primeUser({ publicId: proto.getCreatedBy() }),
      dateCreated: safeTimestampToDate(proto.getDateCreated()),
      includedItems: mapBundleProtoToIncludedItemsProps(proto),
      archivedBy: archivedBy
        ? this._primeUser({ publicId: archivedBy.getValue() })
        : null,
      dateArchived: dateArchived
        ? (timestampToDate(dateArchived) ?? null)
        : null,
      lastEditedBy: lastEditedBy
        ? this._primeUser({ publicId: lastEditedBy.getValue() })
        : null,
      dateLastEdited: dateLastEdited
        ? (timestampToDate(dateLastEdited) ?? null)
        : null,
      baseItem: baseItem ? mapBundleBaseItemProtoToProps(baseItem) : null,
      archivedItemsCount: proto.getArchivedItemsCount(),
    }
  }
}
